"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import { NavigationButtons } from "@/ui/SwiperMultiDevice/SwiperMultiDevice"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import ReactPlayer from "react-player"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { MediaGalleryCardProps } from "../types"

// Styled components for the gallery grid
const GalleryGrid = styled(Box)`
  ${mediaQueries.tabletDown} {
    height: auto;
    flex-direction: column;
  }
  ${mediaQueries.tablet} {
    height: auto;
    flex-direction: row;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    padding-bottom: 120px;
    gap: ${sizes.baseSingle};
  }
`

const MediaItemContainer = styled(Box)`
  position: relative;
  overflow: hidden;
  border-radius: ${sizes.borderRadius};
  background-color: ${colors.lightestGray};

  img {
    transition: transform 0.3s ease;
    object-fit: cover;
  }

  &:hover img {
    transform: scale(1.05);
  }
`

const RightImageContainer = styled(MediaItemContainer)`
  width: 100%;
  height: 300px;

  ${mediaQueries.desktopM} {
    width: 344px;
    height: 527px;
  }
  ${mediaQueries.desktopXl} {
    width: 370px;
    height: 570px;
  }
`

const RightTextContainer = styled(Box)`
  width: 100%;
  height: auto;
  text-align: left;
  ${mediaQueries.tablet} {
    width: auto;
  }
`

const MiddleContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${sizes.baseSingle};
  align-items: flex-end;
  ${mediaQueries.tabletDown} {
    width: 100%;
    height: auto;
  }
`
const RightContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  height: 100%;
  align-items: flex-end;
  gap: ${sizes.baseSingle};

  ${mediaQueries.desktopM} {
    width: 400px;
  }
  ${mediaQueries.desktopXl} {
    width: 430px;
  }
`

const MiddleImageContainer = styled(MediaItemContainer)`
  width: 100%;
  height: 200px;
  ${mediaQueries.desktopM} {
    width: 240px;
    height: 204px;
  }
  ${mediaQueries.desktopXl} {
    width: 309px;
    height: 260px;
  }
`

const MiddleVideoContainer = styled(Box)`
  position: relative;
  width: 100%;
  border-radius: ${sizes.borderRadius};
  overflow: hidden;

  ${mediaQueries.desktopM} {
    width: 357px;
    height: 615px;
  }
  ${mediaQueries.desktopXl} {
    width: 443px;
    height: 763px;
  }
`

const LeftContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseSingle};

  ${mediaQueries.tabletDown} {
    width: 100%;
    height: auto;
  }
`

const UpperLeftImageContainer = styled(MediaItemContainer)`
  width: 100%;
  height: 200px;

  ${mediaQueries.desktopM} {
    width: 300px;
    height: 460px;
  }
  ${mediaQueries.desktopXl} {
    width: 370px;
    height: 570px;
  }
`

const BottomLeftImageContainer = styled(MediaItemContainer)`
  width: 100%;
  height: 60%;

  ${mediaQueries.desktopM} {
    width: 300px;
    height: 240px;
  }
  ${mediaQueries.desktopXl} {
    width: 370px;
    height: 300px;
  }
`
const StyledReactPlayer = styled(
  ({ className, ...props }: { className?: string; [key: string]: any }) => (
    <ReactPlayer className={className} {...props} />
  ),
)`
  border-radius: ${sizes.borderRadiusLarge};
  height: 100% !important;
  width: 100% !important;
`
const StyledDescription = styled(Typography)`
  padding-top: 24px;
  // text-align: left;
`
const StyledTitle = styled(Typography)`
  text-align: left;
`
const StyledNavigationButtons = styled(NavigationButtons)`
  ${mediaQueries.desktopM} {
    justify-content: flex-start;
    padding-bottom: ${sizes.baseDeca};
    button {
      padding: 0;
    }
  }
`

const MediaGalleryCard = ({ item, text, title }: MediaGalleryCardProps) => {
  const t = useTranslations("SEO")
  const {
    rightImage,
    middleImage,
    middleVideo,
    upperLeftImage,
    bottomLeftImage,
  } = item

  const ArrowRightIcon = iconsMap["arrow_lightOrange_black_line"]

  return (
    <GalleryGrid>
      {/** Right Text Container */}
      <RightContainer>
        <RightTextContainer>
          {rightImage && (
            <>
              <StyledTitle variant="h2" color="black">
                {title}
              </StyledTitle>
              <StyledDescription>{text}</StyledDescription>
            </>
          )}
        </RightTextContainer>

        {/* <NavigationButtons  ArrowIcon={arrowIconR}/> */}
        <StyledNavigationButtons ArrowIcon={<ArrowRightIcon />} />

        {/* Right Image */}
        <RightImageContainer>
          {rightImage && (
            <Image src={rightImage.src} alt={rightImage.alt} fill />
          )}
        </RightImageContainer>
      </RightContainer>

      {/* Middle Container */}
      <MiddleContainer>
        {/* Middle Image */}
        <MiddleImageContainer>
          {middleImage && (
            <Image src={middleImage.src} alt={middleImage.alt} fill />
          )}
        </MiddleImageContainer>

        {/* Middle Video */}
        <MiddleVideoContainer>
          {middleVideo && middleVideo.href && (
            // <VideoContainer url={middleVideo.href} />
            <StyledReactPlayer controls url={middleVideo.href} />
          )}
        </MiddleVideoContainer>
      </MiddleContainer>

      {/* Left Container */}
      <LeftContainer>
        {/* Upper Left Image */}
        <UpperLeftImageContainer>
          {upperLeftImage && (
            <Image src={upperLeftImage.src} alt={upperLeftImage.alt} fill />
          )}
        </UpperLeftImageContainer>

        {/* Bottom Left Image */}
        <BottomLeftImageContainer>
          {bottomLeftImage && (
            <Image src={bottomLeftImage.src} alt={bottomLeftImage.alt} fill />
          )}
        </BottomLeftImageContainer>
      </LeftContainer>
    </GalleryGrid>
  )
}

export default MediaGalleryCard
